import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ListTree, ChevronDown } from "lucide-react";
import PageBlobs from "@/components/PageBlobs";
import SiteFooter from "@/components/SiteFooter";

export const SUPPORT_EMAIL = "abhyasedu.support@gmail.com";

export const EmailLink = ({ className = "" }) => (
  <a
    href={`mailto:${SUPPORT_EMAIL}`}
    data-testid="support-email-link"
    className={`font-bold text-[#16A34A] hover:underline ${className}`}
  >
    {SUPPORT_EMAIL}
  </a>
);

export const LList = ({ items }) => (
  <ul className="list-disc space-y-1.5 pl-5">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const TocItem = ({ title, active, onClick, testid }) => (
  <a
    href={`#${testid}`}
    data-testid={`toc-${testid}`}
    onClick={onClick}
    className={`block border-l-2 py-1.5 pl-3.5 text-[13px] font-semibold leading-snug transition-colors duration-200 ${
      active
        ? "border-[#16A34A] text-[#16A34A]"
        : "border-[#E3ECE6] text-[#647067] hover:border-[#16A34A]/40 hover:text-[#17201A]"
    }`}
  >
    {title}
  </a>
);

const LegalPage = ({ title, intro, sections, testid }) => {
  const [active, setActive] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const ids = sections.map((_, i) => `section-${i}`);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.idx));
        });
      },
      { rootMargin: "-25% 0px -65% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testid]);

  const scrollTo = (i) => (e) => {
    e.preventDefault();
    setTocOpen(false);
    document
      .getElementById(ids[i])
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative min-h-screen bg-[#F8FAF7] font-body text-[#17201A]">
      <header className="sticky top-0 z-40 border-b border-[#E3ECE6] bg-[#F8FAF7]/85 backdrop-blur-md">
        <div className="mx-auto flex w-[92%] max-w-[1100px] items-center justify-between py-4">
          <Link
            to="/"
            data-testid="legal-logo-home-link"
            className="font-display text-2xl font-extrabold"
          >
            <img src="/logo.png" alt="Abhyas" className="h-10 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-bold text-[#17201A] transition-[border-color,color] duration-200 hover:border-[#16A34A]/40 hover:text-[#16A34A]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main data-testid={testid} className="relative mx-auto w-[92%] max-w-[1100px] pb-20">
        {/* Document header */}
        <div className="border-b border-[#E3ECE6] pb-8 pt-10 sm:pt-14">
          <h1 className="font-display text-3xl font-extrabold tracking-[-0.01em] sm:text-4xl">
            {title}
          </h1>
          <p className="mt-2.5 text-xs font-semibold uppercase tracking-widest text-[#647067]">
            Last updated: September 2026
          </p>
          {intro && (
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#647067] sm:text-base">
              {intro}
            </p>
          )}
        </div>

        {/* Mobile TOC */}
        <div className="mt-6 lg:hidden">
          <button
            data-testid="toc-mobile-toggle"
            onClick={() => setTocOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-2xl border border-[#E3ECE6] bg-white px-5 py-3.5 text-sm font-bold text-[#17201A]"
          >
            <span className="inline-flex items-center gap-2">
              <ListTree className="h-4 w-4 text-[#16A34A]" />
              On this page
            </span>
            <ChevronDown
              className={`h-4 w-4 text-[#647067] transition-transform duration-200 ${
                tocOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {tocOpen && (
            <div
              data-testid="toc-mobile-menu"
              className="mt-2 rounded-2xl border border-[#E3ECE6] bg-white px-4 py-3"
            >
              {sections.map((s, i) => (
                <TocItem
                  key={ids[i]}
                  title={s.title}
                  active={active === i}
                  onClick={scrollTo(i)}
                  testid={ids[i]}
                />
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-10 pt-8 lg:grid-cols-[230px_1fr] lg:gap-14 lg:pt-12">
          {/* Desktop sticky TOC */}
          <aside className="hidden lg:block">
            <nav
              data-testid="toc-desktop"
              className="sticky top-24 self-start"
              aria-label="Table of contents"
            >
              <p className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#17201A]/50">
                <ListTree className="h-3.5 w-3.5 text-[#16A34A]" />
                On this page
              </p>
              <div className="max-h-[70vh] space-y-0.5 overflow-y-auto pr-2">
                {sections.map((s, i) => (
                  <TocItem
                    key={ids[i]}
                    title={s.title}
                    active={active === i}
                    onClick={scrollTo(i)}
                    testid={ids[i]}
                  />
                ))}
              </div>
            </nav>
          </aside>

          {/* Document content */}
          <article className="max-w-2xl">
            {sections.map((s, i) => (
              <section
                key={ids[i]}
                id={ids[i]}
                data-idx={i}
                data-testid={`policy-section-${i}`}
                className={`scroll-mt-28 ${i > 0 ? "mt-10 border-t border-[#E3ECE6] pt-10" : ""}`}
              >
                <h2 className="mb-4 font-display text-xl font-extrabold text-[#17201A]">
                  {s.title}
                </h2>
                <div className="space-y-3.5 text-sm leading-[1.8] text-[#647067] sm:text-[15px]">
                  {s.body}
                </div>
              </section>
            ))}
          </article>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default LegalPage;
